import { Box, Chip, Radio, styled } from '@planne-software/uni/mui/material';
import { uni } from '@planne-software/uni/v2';

import { uniTypography } from '@src/fonts/typography/uniTypography';
import { cardBrandsStackBreakpoint, tabletBreakpoint } from '@src/common/utils/mediaQuery';

export const BrandList = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: uni('scale-200'),
  minWidth: 0,
  [cardBrandsStackBreakpoint]: {
    '[data-stacked="true"] &': {
      flex: '1 1 auto',
      justifyContent: 'space-between',
    },
  },
}));

export const BrandBadge = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '34px',
  height: '24px',
  flexShrink: 0,
  '& > span': {
    display: 'flex',
    marginRight: 0,
  },
  '& svg': {
    display: 'block',
    maxWidth: '100%',
    maxHeight: '100%',
  },
}));

/**
 * Payments draws its own chip. It looks like the one the collection uses, and that is a
 * coincidence of the design, not a shared rule: isolating them keeps a change to one from
 * reaching the other.
 */
export const MethodChip = styled(Chip)(() => ({
  height: 'auto',
  flexShrink: 0,
  borderRadius: uni('border-radius-round'),
  borderWidth: '1px',
  borderStyle: 'solid',
  '& .MuiChip-label': {
    ...uniTypography('text-sm', 'medium'),
    padding: `${uni('scale-50')} ${uni('scale-200')}`,
  },
  '&[data-tone="success"]': {
    borderColor: uni('border-success-subtle-rest'),
    backgroundColor: uni('bg-success-subtle-rest'),
    color: uni('fg-success-on-color-subtle-rest'),
  },
  '&[data-tone="primary"]': {
    borderColor: uni('border-primary-subtle-rest'),
    backgroundColor: uni('bg-primary-subtle-rest'),
    color: uni('fg-primary-on-color-subtle-rest'),
  },
  [tabletBreakpoint]: {
    '[data-stacked="false"] & .MuiChip-label': {
      ...uniTypography('text-xs', 'medium'),
      padding: `${uni('scale-50')} ${uni('scale-100')}`,
    },
  },
}));

export const AccordionRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  borderRadius: uni('border-radius-xs'),
  border: `1px solid ${uni('border-neutral-subtle-rest')}`,
  backgroundColor: uni('bg-neutral-default-rest'),
}));

export const MethodRow = styled('label')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: uni('scale-200'),
  width: '100%',
  padding: uni('padding-card-components'),
  cursor: 'pointer',
  [cardBrandsStackBreakpoint]: {
    '[data-stacked="true"] > &': {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
}));

export const MethodSelect = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: uni('scale-200'),
  flex: '1 1 auto',
  minWidth: 'min-content',
}));

export const MethodRadio = styled(Radio)(() => ({
  padding: uni('scale-150'),
  '& svg': {
    width: '20px',
    height: '20px',
  },
  [tabletBreakpoint]: {
    padding: uni('scale-200'),
    '& svg': {
      width: '16px',
      height: '16px',
    },
  },
}));

export const AccordionTitleGroup = styled(Box)(() => ({
  display: 'flex',
  flex: '1 1 auto',
  alignItems: 'center',
  gap: uni('scale-100'),
  minWidth: 'min-content',
}));

export const AccordionIcon = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box',
  width: '24px',
  height: '24px',
  color: uni('fg-neutral-action-default-rest'),
  '& svg': {
    display: 'block',
    width: '83.333%',
    height: '83.333%',
  },
  [tabletBreakpoint]: {
    width: '20px',
    height: '20px',
  },
}));

export const AccordionTitle = styled('span')`
  ${uniTypography('text-md', 'semibold')}
  color: ${uni('fg-neutral-action-default-rest')};
  word-break: break-word;

  ${tabletBreakpoint} {
    ${uniTypography('text-sm', 'semibold')}
  }
`;

export const AccordionMeta = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: uni('scale-200'),
  flex: '0 0 auto',
  minWidth: 'min-content',
  [cardBrandsStackBreakpoint]: {
    '[data-stacked="true"] > * > &, [data-stacked="true"] > &': {
      width: '100%',
      justifyContent: 'space-between',
    },
  },
}));

export const AccordionBody = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-400'),
  padding: `${uni('scale-200')} ${uni('padding-card-components')} ${uni('padding-card-components')}`,
}));

export const MethodList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('spacing-24-16'),
  width: '100%',
}));

export const MethodListState = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
}));

export const DisclaimerCard = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: uni('scale-300'),
  width: '100%',
  padding: uni('padding-content'),
  borderRadius: uni('border-radius-md'),
  border: `1px solid ${uni('border-static-content-highlight')}`,
  backgroundColor: uni('bg-static-content-highlight'),
  [tabletBreakpoint]: {
    gap: uni('scale-400'),
    borderRadius: uni('border-radius-sm'),
  },
}));

export const DisclaimerIcon = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box',
  width: '24px',
  height: '24px',
  color: uni('fg-neutral-static-default-heading'),
  '& svg': {
    display: 'block',
    width: '83.333%',
    height: '83.333%',
  },
}));

export const DisclaimerText = styled('p')`
  ${uniTypography('text-md', 'medium')}
  color: ${uni('fg-neutral-static-default-heading')};
  margin: 0;
  width: 100%;
  word-break: break-word;
`;
